import {
  type InfiniteData,
  useMutation,
  useQueryClient,
} from '@tanstack/react-query';

import { connectorService } from '@/services/connector-service';
import type { ConnectorFeature } from '@/types/core/connector';
import type { Page } from '@/types/shared/page';
import { pollUntilResourceNewerThan } from '@/utils/poll';
import type { UUID } from '@/utils/uuid';

import { connectorMutationKeys, connectorQueryKeys } from './query-keys';

export type ReconcileConnectorFeaturePayload = {
  featureId: UUID;
  enabled?: boolean;
  options?: Record<string, unknown>;
  /**
   * Secrets to bind, keyed by credential slot name. Additive (upsert): only the
   * listed slots are added/updated; omitted slots are left unchanged. To remove a
   * binding use useUnbindConnectorFeatureCredential.
   */
  secretIds?: Record<string, UUID>;
  requirements?: Array<string>;
};

/**
 * Applies an additive slot-keyed secret patch onto an existing secretIds map.
 * Reconcile never clears a slot, so this only adds/updates the listed slots.
 */
function mergeSecretIds(
  current: Readonly<Record<string, UUID>> | undefined,
  patch: Record<string, UUID>
): Record<string, UUID> {
  return { ...(current ?? {}), ...patch };
}

/**
 * Upserts connector feature state (reconcileConnectorFeature). Only include fields to change.
 */
export function useReconcileConnectorFeature(
  tenantId?: UUID,
  connectorId?: UUID
) {
  const qc = useQueryClient();

  return useMutation({
    mutationKey: connectorMutationKeys.reconcileConnectorFeature(
      tenantId,
      connectorId
    ),
    mutationFn: async (payload: ReconcileConnectorFeaturePayload) => {
      if (!tenantId) throw new Error('Missing tenant id');
      if (!connectorId) throw new Error('Missing connector id');

      // Capture the current _rev before the mutation so we can poll for a newer one.
      const cachedFeature = qc.getQueryData<ConnectorFeature | null>(
        connectorQueryKeys.featureDetail(
          tenantId,
          connectorId,
          payload.featureId
        )
      );
      const prevRev = cachedFeature?._rev ?? -2;

      const ok = await connectorService.reconcileConnectorFeature(
        tenantId,
        connectorId,
        payload.featureId,
        {
          ...(payload.enabled !== undefined && { enabled: payload.enabled }),
          ...(payload.options !== undefined && { options: payload.options }),
          ...(payload.secretIds !== undefined && {
            secretIds: payload.secretIds,
          }),
          ...(payload.requirements !== undefined && {
            requirements: payload.requirements,
          }),
        }
      );
      if (!ok) throw new Error('Failed to reconcile connector feature');

      // Poll until the read model reflects the change via a newer _rev.
      try {
        await pollUntilResourceNewerThan(
          () =>
            connectorService.findConnectorFeature(
              tenantId,
              connectorId,
              payload.featureId
            ),
          prevRev,
          { maxAttempts: 10, delayMs: 1000 }
        );
      } catch (error) {
        console.warn('Connector feature verification timed out:', error);
      }

      return payload;
    },
    onSuccess: (payload) => {
      qc.setQueriesData<InfiniteData<Page<ConnectorFeature>>>(
        {
          queryKey: connectorQueryKeys.featureListByConnector(
            tenantId,
            connectorId
          ),
        },
        (current) => {
          if (!current) return current;
          return {
            ...current,
            pages: current.pages.map((page) => ({
              ...page,
              items: page.items.map((item) =>
                item.featureId === payload.featureId
                  ? {
                      ...item,
                      ...(payload.enabled !== undefined
                        ? { status: payload.enabled }
                        : {}),
                      ...(payload.options !== undefined
                        ? { options: payload.options }
                        : {}),
                      ...(payload.secretIds !== undefined
                        ? {
                            secretIds: mergeSecretIds(
                              item.secretIds,
                              payload.secretIds
                            ),
                          }
                        : {}),
                      ...(payload.requirements !== undefined
                        ? { requirements: payload.requirements }
                        : {}),
                    }
                  : item
              ),
            })),
          };
        }
      );

      qc.setQueryData<ConnectorFeature | null>(
        connectorQueryKeys.featureDetail(
          tenantId,
          connectorId,
          payload.featureId
        ),
        (current) =>
          current
            ? {
                ...current,
                ...(payload.enabled !== undefined
                  ? { status: payload.enabled }
                  : {}),
                ...(payload.options !== undefined
                  ? { options: payload.options }
                  : {}),
                ...(payload.secretIds !== undefined
                  ? {
                      secretIds: mergeSecretIds(
                        current.secretIds,
                        payload.secretIds
                      ),
                    }
                  : {}),
                ...(payload.requirements !== undefined
                  ? { requirements: payload.requirements }
                  : {}),
              }
            : current
      );
      qc.invalidateQueries({
        queryKey: connectorQueryKeys.featureDetail(
          tenantId,
          connectorId,
          payload.featureId
        ),
      });
      // Reconciling a feature changes the same fields the dashboard's
      // configuration-status snapshot is derived from (`status`, `secretId`),
      // so the "Setup required" badge stays stale unless we refresh it here.
      qc.invalidateQueries({
        queryKey: connectorQueryKeys.configurationStatus(tenantId, connectorId),
      });
    },
  });
}
